import { Controller } from "@hotwired/stimulus";

export class PurchaseTicketController extends Controller {
  static targets = [
    // Modal
    'confirmModal',
    'pointPurchaseModal',
    'convertToPointsModal',
    // PurchaseTicket
    'purchaseTicketUnitField',
    'numberOfTicket',
    'ticketTermsCheckbox'
  ]

  static values = {
    price: Number,
    availablePoints: Number,
    availableHarvests: Number
  }

  // ============================================================
  // Stimulusライフサイクルイベント
  // ============================================================

  /**
   * コントローラー初期化
   * トリガー: コントローラーがDOMに接続された時（ページ読み込み時）
   * 実行タイミング: Stimulusフレームワークによる自動実行
   */
  connect() {
    this.availablePoints = this.availablePointsValue;
    this.availableHarvests = this.availableHarvestsValue;

    this.purchaseTicketUnitFieldTarget.addEventListener(
      "input", this.updateByPurchaseTicketUnitField.bind(this));

    // ページロード時に枚数を初期化（デフォルト値と連動）
    this.updateByPurchaseTicketUnitField();
  }

  /**
   * コントローラー破棄
   * トリガー: コントローラーがDOMから切断された時
   * 実行タイミング: Stimulusフレームワークによる自動実行
   */
  disconnect() {
    this.purchaseTicketUnitFieldTarget.removeEventListener(
      "input", this.updateByPurchaseTicketUnitField.bind(this));
  }

  // ============================================================
  // チケット購入フロー（シナリオ判定とモーダル起動）
  // ============================================================

  /**
   * チケット数量変更時の処理
   * 概要: 「このチケット数量だと合計いくら？枚数はいくつ？」を即座に画面に反映
   * トリガー: チケット数量入力フィールドのinputイベント
   * HTML: app/views/customer/projects/applications/_new_overview.html.erb:73
   *       data-purchase-ticket-target="purchaseTicketUnitField"
   * イベント登録: connect()内で addEventListener
   */
  updateByPurchaseTicketUnitField() {
    const unit = Number(this.purchaseTicketUnitFieldTarget.value);
    if (unit === '') { return }

    // メイン画面の枚数表示を更新（例: 10枚）
    this.numberOfTicketTarget.textContent = unit;
  }

  /**
   * チケット購入申込Submit
   * トリガー: メインフォームのsubmitイベント
   * HTML: app/views/customer/projects/applications/_new_overview.html.erb:7
   *       data: { action: 'submit->purchase-ticket#onPurchaseSubmit' }
   * 動作: バリデーション後、openModal()を呼び出してシナリオ判定
   */
  onPurchaseSubmit(event) {
    event.preventDefault();
    if (this.purchaseTicketUnitFieldTarget.value === '' || !this.ticketTermsCheckboxTarget.checked) {
      return;
    }

    this.openModal();
  }

  /**
   * 収穫金変換成功イベントのハンドラー
   * トリガー: ConvertPointsModalControllerからdispatchされる'convert-points-modal:converted'イベント
   * HTML: _convert_to_point_modal.html.erb
   *       data-action="convert-points-modal:converted->purchase-ticket#onConvertPointsSuccess"
   * 動作: 変換成功後、残高を更新して再度シナリオ判定
   */
  onConvertPointsSuccess(event) {
    // 子コントローラーから通知された最新の残高を更新
    this.availablePoints = event.detail.availablePoints;
    this.availableHarvests = event.detail.availableHarvests;

    // 残高更新後、再度シナリオ判定してモーダル表示
    this.openModal();
  }

  // ============================================================
  // ヘルパーメソッド（内部呼び出し専用）
  // ============================================================

  /**
   * 適切なモーダルを開く（シナリオ判定）
   * 呼び出し元: onPurchaseSubmit(), onConvertPointsSuccess()
   * 動作: 所持ポイント・収穫金の状況に応じて表示するモーダルを決定
   *   - シナリオ(3): ポイント十分 → confirmModal（チケット購入確認）
   *   - シナリオ(2): ポイント不足 & 収穫金あり → convertToPointsModal（収穫金変換）
   *   - シナリオ(1): 両方不足 → pointPurchaseModal（ポイント購入）
   */
  openModal() {
    const unit = Number(this.purchaseTicketUnitFieldTarget.value);
    const totalPrice = this.getTotalPrice(unit);
    const insufficientPoints = this.getInsufficientPoints(unit);

    if (totalPrice <= this.availablePoints) {
      // シナリオ(3): 所持ポイントが購入金額を上回る場合は確認画面に進む
      const confirmModalController = this.application.getControllerForElementAndIdentifier(
        this.confirmModalTarget, 'confirm-modal'
      );
      confirmModalController.show({ unit, totalPrice });
    } else if (this.availableHarvests > 0) {
      // シナリオ(2): 収穫金を所持している場合はポイント変換に進む
      const convertPointsModalController = this.application.getControllerForElementAndIdentifier(
        this.convertToPointsModalTarget, 'convert-points-modal'
      );
      convertPointsModalController.show({ insufficientPoints });
    } else {
      // シナリオ(1): ポイントと収穫金を合わせても足りない場合はポイント購入へ進む
      const pointPurchaseModalController = this.application.getControllerForElementAndIdentifier(
        this.pointPurchaseModalTarget, 'point-purchase-modal'
      );
      pointPurchaseModalController.show({ insufficientPoints });
    }
  }

  /**
   * ポイント不足額を計算
   * 呼び出し元: openModal()
   */
  getInsufficientPoints(unit) {
    return this.getTotalPrice(unit) - this.availablePoints;
  }

  /**
   * 合計金額を計算
   * 呼び出し元: openModal(), getInsufficientPoints()
   */
  getTotalPrice(unit) {
    return unit * 10000
  }
}
